import React, { useState, useEffect, useRef } from "react";
import styles from "./Header.module.scss";
import { ReactComponent as Logo } from "../../assets/icons/header/logo.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/header/close_icon.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/training/pagination_arrow.svg";
import { useLocation } from "react-router-dom";
import { fetchPhones } from "../../services/branchService";

const Header = ({ onOpenPopup }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState(false);
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const phoneDropdownRef = useRef(null);

  const navigationItems = [
    { id: 1, label: "Главная", href: "/" },
    { id: 2, label: "Обучение", href: "/training" },
    { id: 3, label: "Информация", href: "/info" },
    { id: 4, label: "Отзывы", href: "/reviews" },
    { id: 5, label: "Контакты", href: "/contacts" },
  ];

  useEffect(() => {
    const loadPhones = async () => {
      try {
        setLoading(true);
        const phonesData = await fetchPhones();

        const formattedPhones = phonesData
          .sort((a, b) => a.display_order - b.display_order)
          .map((phone) => ({
            number: formatPhoneNumber(phone.phone_number),
            href: `tel:${phone.phone_number.replace(/\D/g, "")}`,
          }));

        setPhones(formattedPhones);
        setError(null);
      } catch (err) {
        setError("Не удалось загрузить контакты");
        setPhones([]);
      } finally {
        setLoading(false);
      }
    };

    loadPhones();
  }, []);

  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (
      cleaned.length === 11 &&
      (cleaned.startsWith("7") || cleaned.startsWith("8"))
    ) {
      const part1 = cleaned.substring(1, 4);
      const part2 = cleaned.substring(4, 7);
      const part3 = cleaned.substring(7, 9);
      const part4 = cleaned.substring(9);
      return `+7 (${part1}) ${part2}-${part3}-${part4}`;
    }

    return phoneNumber;
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        phoneDropdownRef.current &&
        !phoneDropdownRef.current.contains(event.target)
      ) {
        setIsPhoneDropdownOpen(false);
      }
    };

    if (isPhoneDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPhoneDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.burger}`)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isMenuOpen]);

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  const handleCallButtonClick = () => {
    onOpenPopup();
    setIsPhoneDropdownOpen(false);
  };

  const handleMenuCallButtonClick = () => {
    setIsMenuOpen(false);
    onOpenPopup();
  };

  const handlePhoneClick = (e) => {
    if (window.innerWidth <= 768) {
      return;
    }
    e.preventDefault();
    setIsPhoneDropdownOpen(!isPhoneDropdownOpen);
  };

  const allPhones = phones;

  return (
    <>
      {isMenuOpen && (
        <div
          className={styles.menu__overlay}
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <header
        ref={headerRef}
        className={`
          ${styles.header} 
          ${isScrolled ? styles.header__scrolled : ""}
          ${isMenuOpen ? styles.header__menuOpen : ""}
        `}
      >
        <div className={styles.header__container}>
          <a href="/" className={styles.header__brand}>
            <Logo
              className={styles.header__logo}
              aria-label="Логотип Реутовской школы водительского мастерства"
            />
            <h1 className={styles.header__title}>
              Реутовская школа водительского мастерства
            </h1>
          </a>

          <nav
            ref={menuRef}
            id="main-navigation"
            className={`${styles.header__nav} ${
              isMenuOpen ? styles.header__nav__open : ""
            }`}
            aria-label="Основная навигация"
          >
            <button
              className={styles.menu__close}
              onClick={() => setIsMenuOpen(false)}
              aria-label="Закрыть меню"
            >
              <CloseIcon className={styles.menu__closeIcon} />
            </button>

            <div className={styles.menu__content}>
              <ul className={styles.nav__list}>
                {navigationItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.id} className={styles.nav__item}>
                      <a
                        href={item.href}
                        className={`${styles.nav__link} ${
                          isActive ? styles.nav__link__active : ""
                        }`}
                        aria-current={isActive ? "page" : undefined}
                        onClick={handleNavClick}
                      >
                        {item.label}
                      </a>
                    </li>
                  );
                })}
              </ul>

              <div className={styles.menu__contacts}>
                <p className={styles.menu__contact__title}>Контакты</p>

                {loading ? (
                  <div className={styles.menu__message}>
                    <p className={styles.menu__message__text}>
                      Загрузка контактов...
                    </p>
                  </div>
                ) : error ? (
                  <div className={styles.menu__message}>
                    <p
                      className={`${styles.menu__message__text} ${styles.menu__message__error}`}
                    >
                      {error}
                    </p>
                    <p className={styles.menu__message__help}>
                      Пожалуйста, попробуйте позже
                    </p>
                  </div>
                ) : phones.length > 0 ? (
                  <>
                    {phones.map((phone, index) => (
                      <a
                        key={index}
                        href={phone.href}
                        className={styles.menu__contact__phone}
                      >
                        {phone.number}
                      </a>
                    ))}
                  </>
                ) : (
                  <div className={styles.menu__message}>
                    <p className={styles.menu__message__text}>
                      Контактная информация временно недоступна
                    </p>
                  </div>
                )}

                <button
                  className={styles.menu__callButton}
                  onClick={handleMenuCallButtonClick}
                >
                  Заказать звонок
                </button>
              </div>
            </div>
          </nav>
        </div>

        <div className={styles.header__rightBlock}>
          <button
            className={styles.header__callButton}
            onClick={handleCallButtonClick}
          >
            Заказать звонок
          </button>

          <div
            className={`${styles.header__phoneWrapper} ${
              isMenuOpen ? styles.header__phoneWrapper__hidden : ""
            }`}
            ref={phoneDropdownRef}
          >
            {!loading && !error && allPhones.length > 0 && (
              <>
                <div
                  className={`${styles.header__phoneTrigger} ${
                    isPhoneDropdownOpen ? styles.header__phoneTrigger__open : ""
                  }`}
                  onClick={handlePhoneClick}
                  aria-expanded={isPhoneDropdownOpen}
                >
                  <span className={styles.header__phoneTrigger__number}>
                    {allPhones[0].number}
                  </span>
                  {allPhones.length > 1 && (
                    <ArrowIcon
                      className={`${styles.header__phoneTrigger__arrow} ${
                        isPhoneDropdownOpen
                          ? styles.header__phoneTrigger__arrow__open
                          : ""
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </div>

                {allPhones.length > 1 && isPhoneDropdownOpen && (
                  <div className={styles.header__phoneDropdown}>
                    {allPhones.map((phone, index) => (
                      <a
                        key={index}
                        href={phone.href}
                        className={styles.header__phoneDropdown__item}
                      >
                        {phone.number}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <button
          className={`${styles.burger} ${
            isMenuOpen ? styles.burger__open : ""
          }`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Закрыть меню" : "Открыть меню"}
          aria-expanded={isMenuOpen}
          aria-controls="main-navigation"
        >
          <span className={styles.burger__line}></span>
          <span className={styles.burger__line}></span>
          <span className={styles.burger__line}></span>
        </button>
      </header>
    </>
  );
};

export default Header;
